import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModalInfo } from '../../../../shared/models';
import { SystemPages } from '../../../../shared/enums/routerPaths.enum';
import { SelectionService } from 'src/app/modules/system-shared/services/selection.service';
import { FormControl, Validators } from '@angular/forms';
import { Subject, of, timer } from 'rxjs';
import { catchError, delay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ExportService } from 'src/app/shared/services/requests/export.service';
import {
  CSVExportRecord,
  CSVExportState,
  ExportProfilesRequestPayload,
  ProfileExportSetting,
} from 'src/app/shared/models/export';
import { ToggleSettings } from '../../models/toggle-settings';
import { patchState } from 'src/app/shared/utilities/object-patch';

@Component({
  selector: 'app-csv-export-modal',
  templateUrl: './csv-export-modal.component.html',
  styleUrls: ['./csv-export-modal.component.scss'],
  standalone: false,
})
export class CSVExportModalComponent implements OnInit, OnDestroy {
  constructor(
    private dialogRef: MatDialogRef<CSVExportModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      settings: ProfileExportSetting;
      exportRequestQuery: string;
      folderId?: string;
      activeFolderStatuses?: string[];
    },
    private exportService: ExportService,
    private selectionService: SelectionService,
    private cdr: ChangeDetectorRef,
  ) {}

  readonly systemPagesEnum = SystemPages;

  state: 'settings' | 'waiting' | 'success' | 'error' = 'settings';
  infoText: string = '';
  showContactSettings = false;
  withContacts = false;
  progressPercentage = 0;
  toggleData: ToggleSettings | null = null;

  $unsubscribe = new Subject<void>();
  $stopProgressImitation = new Subject<void>();

  info: ModalInfo = {
    type: 'simple-modal',
    closeModalBtnId: 'close-plan-limitation-m',
    header: {
      title: 'EXPORT.EXPORT_MODAL.TITLE',
      text: 'EXPORT.EXPORT_MODAL.SUBTITLE',
    },
    actions: {
      cancel: {
        id: 'm-cancel-export-btn',
      },
      submit: {
        id: 'm-confirm-export-btn',
        extraClass: 'bright',
        type: 'update',
        text: 'Export',
        disabled: true,
      },
    },
  };

  nameControl: FormControl<string>;

  @HostListener('keydown.enter') onEnterPressed(): void {
    if (this.state === 'settings' && this.info.actions.submit.disabled === false) {
      this.onExportSubmited();
    }
  }

  ngOnInit(): void {
    this.showContactSettings = this.data.settings.email || this.data.settings.phone;
    this.withContacts = this.showContactSettings;

    this.initForm();
    this.handleFormValueChanges();
    this.toggleData = this.createToggleSettings();
  }

  toggleContactSetting(): void {
    this.withContacts = !this.withContacts;
    this.toggleData = this.createToggleSettings();
  }

  onExportSubmited(): void {
    this.info = patchState(this.info, {
      header: null,
      actions: null,
    });
    this.state = 'waiting';
    this.infoText = 'EXPORT.EXPORT_MODAL.INFO.IN_PROGRESS';

    const searchQuery = this.data.exportRequestQuery;
    const payload = this.getRequestPaylod();

    this.imitateProgress();

    this.exportService
      .createProfileExport(searchQuery, payload)
      .pipe(
        takeUntil(this.$unsubscribe),
        catchError((e) => of(null)),
      )
      .subscribe((record: CSVExportRecord) => {
        if (!record?.status) {
          this.handleExportFailure();
          return;
        }

        const { status } = record;
        this.handleResponce({ status }, record);
      });
  }

  getRequestPaylod(): ExportProfilesRequestPayload {
    const selection = this.selectionService.getSelection();

    const payload: ExportProfilesRequestPayload = {
      allProfiles: selection.allProfiles,
      exportName: this.nameControl.value?.trim(),
      withContacts: this.withContacts,
    };
    if (selection.allProfiles && this.data.folderId) {
      payload.folderId = this.data.folderId;
      payload.statusIds = this.data.activeFolderStatuses || [];
    }
    if (selection.range) {
      payload.fromRange = selection.range.from;
      payload.toRange = selection.range.to;
    }
    if (selection.ids) {
      payload.ids = Array.from(selection.ids.values());
    }

    return payload;
  }

  handleResponce(exportState: CSVExportState, record: CSVExportRecord): void {
    if (!exportState.status) {
      this.handleExportFailure();
      return;
    }

    const { status } = exportState;

    if (typeof exportState?.percent === 'number') {
      this.$stopProgressImitation.next();
      this.updateProgressPercentage(exportState);
    }

    if (status === 'PENDING' || status === 'IN_PROGRESS') {
      this.waitUntilExportIsResolved(record);
      return;
    }

    if (status === 'SUCCEEDED') {
      this.handleExportSuccess(record);
      return;
    }

    this.handleExportFailure();
  }

  waitUntilExportIsResolved(record: CSVExportRecord): void {
    timer(1000)
      .pipe(
        take(1),
        takeUntil(this.$unsubscribe),
        switchMap(() => this.exportService.getExportState(record.id)),
        tap((exportState: CSVExportState) => {
          this.handleResponce(exportState, record);
        }),
        catchError(() => {
          this.handleExportFailure();
          return of(null);
        }),
      )
      .subscribe();
  }

  handleExportSuccess(record: CSVExportRecord): void {
    this.state = 'success';
    this.infoText = 'EXPORT.EXPORT_MODAL.INFO.SUCCESSFULLY_DOWNLOADED';
    this.exportService.downloadCSVFile(record.id, record.name);
    this.cdr.detectChanges();
  }

  handleExportFailure(): void {
    this.$stopProgressImitation.next();
    this.state = 'error';
    this.infoText = 'EXPORT.EXPORT_MODAL.INFO.EXPORT_FAILED';
    this.cdr.detectChanges();
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  initForm(): void {
    this.nameControl = new FormControl('', Validators.required);
  }

  handleFormValueChanges(): void {
    this.nameControl.valueChanges
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(() => this.inferSubmitState());
  }

  inferSubmitState(): void {
    const disabled = this.nameControl.invalid || this.nameControl.value?.trim() === '';
    this.info = patchState(this.info, ['actions', 'submit'], { disabled });
  }

  updateProgressPercentage(exportState: CSVExportState): void {
    if (!exportState || typeof exportState.percent !== 'number') return;

    this.progressPercentage = exportState.percent;
    this.cdr.detectChanges();
  }

  /** If export is in PENDING state we will increase an percent counter by 1 every second
   * until 10 percent is reached or export status is changed.
   */
  imitateProgress(): void {
    of(null)
      .pipe(delay(1000), takeUntil(this.$unsubscribe), takeUntil(this.$stopProgressImitation))
      .subscribe(() => {
        if (this.progressPercentage >= 10) {
          return;
        }

        this.progressPercentage++;
        this.cdr.markForCheck();
        this.imitateProgress();
      });
  }

  private createToggleSettings(): ToggleSettings {
    return {
      label: 'EXPORT.EXPORT_MODAL.WITH_CONTACTS',
      description: 'EXPORT.EXPORT_MODAL.CONTACT_SETTING_EXPLANATION',
      isChecked: this.withContacts,
      key: 'contactSettings',
    };
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$stopProgressImitation.next();
    this.$unsubscribe.complete();
    this.$stopProgressImitation.complete();
  }
}
